import { useEffect, useRef } from 'react';
import './BrainPanel.css';

// Luruz thinking, live, drawn like a neural-network visualizer - and every
// mark is real data (see gpu-worker/brain/server.py):
//   - left: the last tokens the model wrote;
//   - middle: one column per real layer (40 for granite4:micro), each node
//     one real neuron - always the same 16 of the layer's 2560 - lit by its
//     actual output for this token (divided by the layer's RMS);
//   - right: the 5 candidates the model weighed for this token, with their
//     real probabilities, the one it wrote in yellow.
// Lines join active neurons of neighbouring layers: their brightness comes
// from those two real values, not from a weight (a transformer has no
// single weight between two residual-stream neurons).
// `store` is a ref App.js fills as {"status": "cerebro"} events arrive
// ({modelo, tokens}); this component reads it once per frame, so a fast
// stream never re-renders React.

const COPY = {
  es: {
    caption: (m) => `${m.nombre}${m.capas ? ` · ${m.capas} capas × ${m.ancho} neuronas · ${m.vocab?.toLocaleString('es')} tokens de vocabulario` : ''}`,
    real: (n) => `Todo real: cada columna es una capa y cada punto una de sus neuronas (${n} fijas por capa).`,
    waiting: 'Esperando el primer token…',
    noLayers: 'Este turno lo respondió Ollama: hay candidatos reales, pero no activaciones.',
    layer: 'capa'
  },
  en: {
    caption: (m) => `${m.nombre}${m.capas ? ` · ${m.capas} layers × ${m.ancho} neurons · ${m.vocab?.toLocaleString('en')} vocabulary tokens` : ''}`,
    real: (n) => `All real: each column is a layer and each dot one of its neurons (${n} fixed per layer).`,
    waiting: 'Waiting for the first token…',
    noLayers: 'Ollama answered this turn: real candidates, but no activations.',
    layer: 'layer'
  }
};

const CONTEXT = 10; // last written tokens shown on the left
const C = { bg: '#170d38', in: '#ff9f4a', pos: '#c9a8ff', neg: '#7fc8ff', hit: '#ffe14d', fg: '#ece6ff', muted: '#a99be0', node: '#fff3d6' };
const MONO = 'ui-monospace, "SFMono-Regular", Consolas, monospace';

const show = (tok) => (tok || '').replace(/\n/g, '↵') || '·';
// A neuron's value is in units of its layer's RMS; ~3 RMS reads as fully lit.
const glow = (v) => Math.tanh(Math.abs(v) / 1.5);

export default function BrainPanel({ store, language = 'es' }) {
  const canvasRef = useRef(null);
  const captionRef = useRef(null);
  const realRef = useRef(null);
  const copy = COPY[language] || COPY.es;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let cursor = 0;   // tokens drawn so far, easing toward tokens.length
    let wave = 1;     // 0..1: how far the current token has travelled through the layers
    let lastIndex = -1;
    let raf;

    const frame = () => {
      const { tokens = [], modelo } = store.current || {};
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      if (captionRef.current) captionRef.current.textContent = modelo ? copy.caption(modelo) : '';

      // Catch up faster when far behind, so a burst never lags for long.
      if (cursor > tokens.length) cursor = 0;
      const behind = tokens.length - cursor;
      cursor = reduce ? tokens.length : Math.min(tokens.length, cursor + Math.max(0.35, behind / 12));
      const index = Math.ceil(cursor) - 1;
      if (index !== lastIndex) { lastIndex = index; wave = reduce ? 1 : 0; }
      wave = Math.min(1, wave + 0.08);
      const current = tokens[index];
      const layers = current?.capas || null;
      const nLayers = layers ? layers.length : modelo?.capas || 40;
      const nNodes = layers ? layers[0][1].length : 16;
      if (realRef.current) realRef.current.textContent = layers || !current ? copy.real(nNodes) : copy.noLayers;

      const narrow = w < 520;
      const leftX = narrow ? 64 : 96;
      const rightX = w - (narrow ? 112 : 160);
      const colX = (c) => leftX + 18 + ((rightX - leftX - 36) * c) / Math.max(1, nLayers - 1);
      const top = 14;
      const bottom = h - 18;
      const nodeY = (i, n) => top + ((bottom - top) * (i + 0.5)) / n;
      const r = Math.max(1.6, Math.min(4, (rightX - leftX) / nLayers / 3.2, (bottom - top) / nNodes / 3));
      // The token's pass through the layers, left to right.
      const reached = (c) => Math.max(0, Math.min(1, wave * (nLayers + 4) - c));
      const level = (c, i) => (layers ? glow(layers[c][1][i]) * reached(c) : 0);

      const context = tokens.slice(Math.max(0, index + 1 - CONTEXT), index + 1);
      const alts = current ? current.alts : [];

      ctx.globalCompositeOperation = 'lighter';
      if (layers) {
        // Newest written token -> first layer's active neurons.
        const yIn = nodeY(context.length - 1, CONTEXT);
        for (let i = 0; i < nNodes; i++) {
          const v = level(0, i);
          if (v < 0.15) continue;
          ctx.strokeStyle = C.in;
          ctx.globalAlpha = 0.5 * v;
          ctx.lineWidth = 0.6 + v;
          ctx.beginPath();
          ctx.moveTo(leftX, yIn);
          ctx.bezierCurveTo((leftX + colX(0)) / 2, yIn, (leftX + colX(0)) / 2, nodeY(i, nNodes), colX(0), nodeY(i, nNodes));
          ctx.stroke();
        }
        // Layer -> next layer, between neurons that are both active.
        for (let c = 0; c < nLayers - 1; c++) {
          const x1 = colX(c);
          const x2 = colX(c + 1);
          for (let a = 0; a < nNodes; a++) {
            const va = level(c, a);
            if (va < 0.2) continue;
            for (let b = 0; b < nNodes; b++) {
              const s = va * level(c + 1, b);
              if (s < 0.12) continue;
              ctx.strokeStyle = layers[c][1][a] * layers[c + 1][1][b] >= 0 ? C.pos : C.neg;
              ctx.globalAlpha = Math.min(0.5, s * 0.45);
              ctx.lineWidth = 0.4 + s;
              ctx.beginPath();
              ctx.moveTo(x1, nodeY(a, nNodes));
              ctx.lineTo(x2, nodeY(b, nNodes));
              ctx.stroke();
            }
          }
        }
        // Last layer -> candidates, by each candidate's real probability.
        const xl = colX(nLayers - 1);
        alts.forEach(([, p], k) => {
          for (let a = 0; a < nNodes; a++) {
            const s = level(nLayers - 1, a) * Math.sqrt(p) * reached(nLayers + 2);
            if (s < 0.08) continue;
            ctx.strokeStyle = k === 0 ? C.hit : C.pos;
            ctx.globalAlpha = Math.min(0.55, s * 0.6);
            ctx.lineWidth = 0.4 + s;
            ctx.beginPath();
            ctx.moveTo(xl, nodeY(a, nNodes));
            ctx.bezierCurveTo((xl + rightX) / 2, nodeY(a, nNodes), (xl + rightX) / 2, nodeY(k, 5), rightX, nodeY(k, 5));
            ctx.stroke();
          }
        });
      }
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.textBaseline = 'middle';

      // Neurons: dim at rest, lit by their real value; blue when negative.
      for (let c = 0; c < nLayers; c++) {
        for (let i = 0; i < nNodes; i++) {
          const v = level(c, i);
          ctx.fillStyle = layers && layers[c][1][i] < 0 ? C.neg : C.node;
          ctx.globalAlpha = 0.14 + 0.86 * v;
          ctx.beginPath();
          ctx.arc(colX(c), nodeY(i, nNodes), r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;

      // Layer numbers under the columns.
      ctx.fillStyle = C.muted;
      ctx.font = `9px ${MONO}`;
      ctx.textAlign = 'center';
      const step = narrow ? 10 : 5;
      for (let c = 0; c < nLayers; c++) {
        if (c === 0 || (c + 1) % step === 0) ctx.fillText(c === 0 ? `${copy.layer} 1` : String(c + 1), colX(c), h - 6);
      }

      // Written tokens, newest at the bottom.
      ctx.font = `${narrow ? 11 : 12}px ${MONO}`;
      ctx.textAlign = 'right';
      context.forEach((tok, i) => {
        const y = nodeY(i, CONTEXT);
        const newest = i === context.length - 1;
        ctx.globalAlpha = newest ? 1 : 0.4 + 0.5 * (i / CONTEXT);
        ctx.fillStyle = newest ? C.fg : C.muted;
        ctx.fillText(show(tok.t).slice(0, narrow ? 7 : 11), leftX - 8, y);
        ctx.fillStyle = C.in;
        ctx.beginPath();
        ctx.arc(leftX, y, 3, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;

      // Candidates with their real probabilities; the written one in yellow.
      ctx.textAlign = 'left';
      alts.forEach(([tok, p], k) => {
        const y = nodeY(k, 5);
        const chosen = tok === current.t;
        ctx.fillStyle = chosen ? C.hit : C.node;
        ctx.globalAlpha = 0.25 + 0.75 * Math.max(p, 0.08);
        ctx.beginPath();
        ctx.arc(rightX, y, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
        const label = show(tok).slice(0, narrow ? 8 : 12);
        ctx.font = `${narrow ? 12 : 13}px ${MONO}`;
        const tx = rightX + 12;
        if (chosen) {
          const tw = ctx.measureText(label).width;
          ctx.fillStyle = C.hit;
          ctx.fillRect(tx - 3, y - 9, tw + 6, 15);
          ctx.fillStyle = C.bg;
        } else {
          ctx.fillStyle = C.fg;
        }
        ctx.fillText(label, tx, y - 1);
        ctx.fillStyle = C.muted;
        ctx.font = `10px ${MONO}`;
        ctx.fillText(`${(p * 100).toFixed(p < 0.1 ? 1 : 0)}%`, tx, y + 11);
      });

      if (!tokens.length) {
        ctx.fillStyle = C.muted;
        ctx.font = '12px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(copy.waiting, w / 2, h / 2);
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [store, copy]);

  return (
    <figure className="brain-panel">
      <canvas ref={canvasRef} className="brain-canvas" />
      <figcaption className="brain-caption">
        <span ref={captionRef} className="brain-model" />
        <span ref={realRef} className="brain-honesty" />
      </figcaption>
    </figure>
  );
}
